import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AssignerService } from 'src/app/services/assigner/assigner.service';
import { ServicesService } from 'src/app/services/service/services.service';
import { DriverService } from 'src/app/services/driver/driver.service';
import { DriverModel } from 'src/app/pages/operational-staff/interface/operational-staff.model';
import { DriverTypeApiValue } from 'src/app/enum/driver-type.enum';
import {
  AssignmentFormService,
  AssignmentTransformerService,
  DomManagerService
} from './services';

import {
  AssignmentDriver,
  AssignmentFormData,
  AssignationDriversModalResult,
  Zone
} from './models';
import { AssignationDriversModalComponent } from './components/assignation-drivers-modal/assignation-drivers-modal.component';

@Component({
  selector: 'app-modal-add-assignament',
  templateUrl: './modal-add-assignament.component.html',
  styleUrls: ['./modal-add-assignament.component.css'],
  providers: [
    AssignmentFormService,
    AssignmentTransformerService,
    DomManagerService
  ]
})
export class ModalAddAssignamentComponent implements OnInit, OnDestroy {
  // Form
  assignamentForm!: FormGroup;

  // Data
  zones: Zone[] = [];
  availableDrivers: AssignmentDriver[] = [];
  selectedDrivers: AssignmentDriver[] = [];
  operatives: string[] = [];

  // UI State
  isSubmitting = false;
  isDriversLoading = false;
  private clickCounts: { [key: string]: number } = {};
  private hasManualDriverSelection = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly offcanvasService: NgbOffcanvas,
    private readonly modalService: NgbModal,
    private readonly assignerService: AssignerService,
    private readonly servicesService: ServicesService,
    private readonly driverService: DriverService,
    private readonly formService: AssignmentFormService,
    private readonly transformerService: AssignmentTransformerService,
    private readonly domManager: DomManagerService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.watchZoneChanges();
    this.loadZones();
    this.domManager.initializeDom();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes the form using the form service
   */
  private initializeForm(): void {
    this.assignamentForm = this.formService.createForm();
  }
  private watchZoneChanges(): void {
    this.assignamentForm
      .get('zoneId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((zoneName: string | null) => {
        this.hasManualDriverSelection = false;
        this.loadDriversForZoneName(zoneName);
      });
  }

  /**
   * Loads zones from the service
   */
  private async loadZones(): Promise<void> {
    try {
      const response: any = await firstValueFrom(this.servicesService.getZones());
      this.zones = response.zones;
      this.operatives = response.zones.map((zone: Zone) => zone.zoneName);
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  }
  private async loadDriversForZoneName(
    zoneName: string | null | undefined
  ): Promise<void> {
    const zoneId = this.getZoneIdByName(zoneName);

    if (!zoneId) {
      this.isDriversLoading = false;
      this.clearDriversState();
      this.cdr.markForCheck();
      return;
    }

    this.isDriversLoading = true;
    this.cdr.markForCheck();

    try {
      const allDrivers = await firstValueFrom(
        this.driverService.getAllDrivers()
      );

      const mappedDrivers = this.buildAvailableDrivers(
        allDrivers ?? [],
        zoneId
      );

      this.availableDrivers = mappedDrivers;
      this.updateSelectedDriversAfterReload(mappedDrivers);
      this.updateSelectedDriversControl();
    } catch (error) {
      console.error('Error loading drivers for assignment:', error);
      this.clearDriversState();
    } finally {
      this.isDriversLoading = false;
      this.cdr.markForCheck();
    }
  }

  async openDriversSelectionModal(): Promise<void> {
    if (this.isDriversLoading) {
      return;
    }

    const modalRef = this.modalService.open(AssignationDriversModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'assignation-drivers-modal-window'
    });

    modalRef.componentInstance.drivers = this.availableDrivers;
    modalRef.componentInstance.selectedDrivers = this.selectedDrivers;

    try {
      const result = (await modalRef.result) as
        | AssignationDriversModalResult
        | undefined;

      if (!result) {
        return;
      }

      this.hasManualDriverSelection = true;
      this.selectedDrivers = result.selectedDrivers;
      this.updateSelectedDriversControl();
      this.cdr.markForCheck();
    } catch {}
  }

  private getZoneIdByName(zoneName: string | null | undefined): string | null {
    const selectedZone = this.zones.find((zone) => zone.zoneName === zoneName);
    return selectedZone?.zoneId ?? null;
  }

  private buildAvailableDrivers(
    allDrivers: DriverModel[],
    zoneId: string
  ): AssignmentDriver[] {
    return allDrivers
      .filter(
        (driver: DriverModel) =>
          driver.zoneId === zoneId &&
          driver.isActive &&
          driver.workdayStartTime?.seconds &&
          driver.workdayEndTime?.seconds
      )
      .map((driver: DriverModel) => this.mapDriverToAssignmentDriver(driver))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }

  private updateSelectedDriversAfterReload(
    mappedDrivers: AssignmentDriver[]
  ): void {
    if (!this.hasManualDriverSelection) {
      this.selectedDrivers = [...mappedDrivers];
      return;
    }

    const selectedIds = this.selectedDrivers.map((driver) => driver.driverId);
    this.selectedDrivers = mappedDrivers.filter((driver) =>
      selectedIds.includes(driver.driverId)
    );
  }

  private clearDriversState(): void {
    this.availableDrivers = [];
    this.selectedDrivers = [];
    this.updateSelectedDriversControl();
  }

  private mapDriverToAssignmentDriver(driver: DriverModel): AssignmentDriver {
    const startTime = this.secondsToHour(driver.workdayStartTime?.seconds);
    const endTime = this.secondsToHour(driver.workdayEndTime?.seconds);

    return {
      driverId: driver.driverId,
      fullName: driver.fullName,
      isInternal: driver.isInternal,
      driverType: this.normalizeDriverType(driver.operatorType),
      profilePictureUrl: driver.profilePictureUrl,
      schedule: {
        mode: 'full',
        full: {
          start: startTime,
          end: endTime
        },
        split: this.createSplitSchedule()
      }
    };
  }

  private updateSelectedDriversControl(): void {
    this.assignamentForm.patchValue(
      {
        selectedDrivers: this.selectedDrivers
      },
      { emitEvent: false }
    );
  }

  private normalizeDriverType(type?: string | null): DriverTypeApiValue {
    const values = Object.values(DriverTypeApiValue) as DriverTypeApiValue[];
    return values.includes(type as DriverTypeApiValue)
      ? (type as DriverTypeApiValue)
      : DriverTypeApiValue.NOT_SPECIFIED;
  }

  private secondsToHour(seconds?: string | number): string {
    const totalSeconds = Number(seconds);

    if (Number.isNaN(totalSeconds) || totalSeconds <= 0) {
      return '08:00';
    }

    const date = new Date(totalSeconds * 1000);
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  private createSplitSchedule(): {
    morningStart: string;
    morningEnd: string;
    afternoonStart: string;
    afternoonEnd: string;
  } {
    return {
      morningStart: '',
      morningEnd: '',
      afternoonStart: '',
      afternoonEnd: ''
    };
  }

  private getDriversForRequest(): AssignmentDriver[] {
    const selectedDriversById = new Map(
      this.selectedDrivers.map((driver) => [driver.driverId, driver])
    );

    return this.availableDrivers.map(
      (driver) => selectedDriversById.get(driver.driverId) ?? driver
    );
  }

  /**
   * Handles form submission
   */
  async saveAssignament(): Promise<void> {
    if (!this.formService.isFormValid(this.assignamentForm) || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      const formData = this.assignamentForm.value as AssignmentFormData;
      const driversForRequest = this.getDriversForRequest();
      const assignmentRequest = this.transformerService.transformToRequest(
        formData,
        this.zones,
        driversForRequest
      );
      const alertData = this.transformerService.createAlertData(
        assignmentRequest.targetDate,
        assignmentRequest.zoneId,
        this.zones
      );

      // Notify start of assignment
      this.assignerService.publish('updateElementsAssign', alertData);
      this.offcanvasService.dismiss();

      // Submit assignment
      const result: any = await firstValueFrom(
        this.assignerService.addAssignment(assignmentRequest)
      );

      // Notify result
      if (result.wasSuccessful) {
        this.assignerService.publish('completeElementsAssign', alertData);
      } else {
        this.assignerService.publish('errorElementsAssign', assignmentRequest);
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      const formData = this.assignamentForm.value as AssignmentFormData;
      const driversForRequest = this.getDriversForRequest();
      const assignmentRequest = this.transformerService.transformToRequest(
        formData,
        this.zones,
        driversForRequest
      );
      this.assignerService.publish('errorElementsAssign', assignmentRequest);
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Closes the modal
   */
  closeCanvas(): void {
    this.offcanvasService.dismiss();
  }

  /**
   * Validates numeric input
   */
  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  /**
   * Handles select click for dropdown positioning
   */
  onSelectClick(event: MouseEvent): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectId = selectElement.id;
    this.domManager.handleSelectClick(selectId, this.clickCounts);
  }

  /**
   * Handles select blur event
   */
  onSelectBlur(focused: boolean): void {
    if (!focused) {
      this.domManager.resetSelectCounts(this.clickCounts);
    }
  }

  /**
   * Getter for form validity (used in template)
   */
  get isFormValid(): boolean {
    return this.formService.isFormValid(this.assignamentForm);
  }
}
